import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { date, mixed, number, object, ref, string, bool } from 'yup';
import { complement, hasIn, isNil } from 'ramda';
import { EValidationRule } from '../../constants';
/**
 * Описывает валидацию полей по умолчанию
 * Поддерживает только required
 */

var _defaultValidator = function _defaultValidator(rules) {
  /**
   * Валидатор иммутабельный тип, необходимо его переопределять при изменении правил, либо использовать withMutation
   */
  return rules.reduce(function (validator, rule) {
    if (rule.name === EValidationRule.required) {
      return validator.required(rule.message);
    }

    return validator;
  }, mixed());
};
/**
 * Описывает валидацию полей для типа "строка"
 */


var _stringValidator = function _stringValidator(rules) {
  /**
   * Валидатор иммутабельный тип, необходимо его переопределять при изменении правил, либо использовать withMutation
   */
  return rules.reduce(function (validator, rule) {
    switch (rule.name) {
      case EValidationRule.required:
        return validator.required(rule.message);

      case EValidationRule.pattern:
        return validator.matches(new RegExp(rule.value), rule.message);

      case EValidationRule.gte:
        return validator.min(Number(rule.value), rule.message);

      case EValidationRule.lte:
        return validator.max(Number(rule.value), rule.message);

      case EValidationRule.eq:
        return validator.length(Number(rule.value), rule.message);

      default:
        return validator;
    }
  }, string());
};
/**
 * Описывает валидацию полей для типа "число"
 */


var _numberValidator = function _numberValidator(rules) {
  /**
   * Валидатор иммутабельный тип, необходимо его переопределять при изменении правил, либо использовать withMutation
   */
  return rules.reduce(function (validator, rule) {
    switch (rule.name) {
      case EValidationRule.required:
        return validator.required(rule.message);

      case EValidationRule.gt:
        return validator.moreThan(Number(rule.value), rule.message);

      case EValidationRule.gte:
        return validator.min(Number(rule.value), rule.message);

      case EValidationRule.lt:
        return validator.lessThan(Number(rule.value), rule.message);

      case EValidationRule.lte:
        return validator.max(Number(rule.value), rule.message);

      case EValidationRule.eq:
        {
          return validator.test('equalTo', rule.message, function (value) {
            return Number(value) === Number(rule.value);
          });
        }

      case EValidationRule.eqField:
        {
          return validator.test('equalTo', rule.message, function (value) {
            return Number(value) === Number(rule.field);
          });
        }

      case EValidationRule.gtField:
        return validator.when(rule.field, {
          is: complement(isNil),
          then: validator.moreThan(ref(rule.field), rule.message)
        });

      case EValidationRule.gteField:
        return validator.when(rule.field, {
          is: complement(isNil),
          then: validator.min(ref(rule.field), rule.message)
        });

      case EValidationRule.ltField:
        return validator.when(rule.field, {
          is: complement(isNil),
          then: validator.lessThan(ref(rule.field), rule.message)
        });

      case EValidationRule.lteField:
        return validator.when(rule.field, {
          is: complement(isNil),
          then: validator.max(ref(rule.field), rule.message)
        });

      case EValidationRule.conditionalRequired:
        return validator.when(rule.field, {
          is: isNil,
          then: validator.required(rule.message)
        });

      default:
        return validator;
    }
  }, number());
};
/**
 * Описывает валидацию полей для типа "Дата"
 */


var _dateValidator = function _dateValidator(rules) {
  /**
   * Здесь можно реализовать разные варианты date валидаций (lte, gte и тп),
   * используя validator.test() и разные хелперы из date-fns
   */
  return rules.reduce(function (validator, rule) {
    switch (rule.name) {
      case EValidationRule.required:
        return validator.required(rule.message);

      default:
        return validator;
    }
  }, date().nullable());
};
/**
 * Описывает валидацию полей для типа "булево значение"
 */


var _booleanValidator = function _booleanValidator(rules) {
  return rules.reduce(function (validator, rule) {
    var _rule$value;

    switch (rule.name) {
      case EValidationRule.required:
        return validator.required(rule.message);

      case EValidationRule.eq:
        return validator.oneOf([((_rule$value = rule.value) === null || _rule$value === void 0 ? void 0 : _rule$value.toLowerCase()) === 'true'], rule.message);

      default:
        return validator;
    }
  }, bool());
};
/**
 * В зависимости от типа поля используются разные схемы Yup
 * Для неизвестных типов используется mixed тип
 */


var _getValidatorByType = function _getValidatorByType(type, rules) {
  switch (type) {
    case 'date':
      return _dateValidator(rules);

    case 'number':
      return _numberValidator(rules);

    case 'string':
      return _stringValidator(rules);

    case 'boolean':
      return _booleanValidator(rules);

    default:
      return _defaultValidator(rules);
  }
};
/**
 * Собирает группы зависимых полей, например для conditionalRequired
 * Внимание: Согласно схеме Yup в группе не может быть больше 2 полей
 */


var _getFieldPairs = function _getFieldPairs(validation) {
  var _validation$reduce = validation.reduce(function (_pairs, field) {
    if (field.rules.some(function (rule) {
      return (rule === null || rule === void 0 ? void 0 : rule.name) === EValidationRule.conditionalRequired;
    })) {
      return [].concat(_toConsumableArray(_pairs), [field.name]);
    }

    return _pairs;
  }, []),
      _validation$reduce2 = _slicedToArray(_validation$reduce, 2),
      firstField = _validation$reduce2[0],
      secondField = _validation$reduce2[1];

  return firstField && secondField ? [[firstField, secondField]] : undefined;
};
/**
 * Утиная проверка типизация схемы yup на основании используемого в формике метода валидации
 * https://github.com/formium/formik/blob/master/packages/formik/src/Formik.tsx
 */


var _isYupScheme = function _isYupScheme(validation) {
  return hasIn('validateAt', validation);
};
/**
 * Преобразует схему валидации в формат библиотеки Yup
 * Если в качестве схемы передан объект валидатора Yup - он будет использован "как есть"
 * Возможность передать валидатор сделана для обеспечения возможности создавать свои схемы валидации
 */


export var buildValidationScheme = function buildValidationScheme(validation) {
  if (_isYupScheme(validation)) {
    return validation;
  }

  var shape = validation.reduce(function (_shape, field) {
    return _objectSpread(_objectSpread({}, _shape), {}, _defineProperty({}, field.name, _getValidatorByType(field.type, field.rules.filter(Boolean))));
  }, {});
  return object().shape(shape, _getFieldPairs(validation));
};